import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Wifi, Copy, LogIn, LogOut, ScrollText, Phone, Check, MapPin, Navigation, Car, Caravan, ChevronRight, Camera } from "lucide-react";
import { toast } from "sonner";
import { api, mediaUrl } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { CheckinInstructions } from "@/components/guest/CheckinInstructions";

function Section({ icon: Icon, title, children, testid }) {
  return (
    <div data-testid={testid} className="bg-white rounded-3xl p-7 sm:p-8 border border-black/5 shadow-sm">
      <div className="flex items-center gap-3 mb-5">
        <div className="h-11 w-11 rounded-xl bg-brand-sand flex items-center justify-center">
          <Icon size={22} className="text-brand-terracotta" />
        </div>
        <h2 className="font-display text-2xl tracking-tight">{title}</h2>
      </div>
      {children}
    </div>
  );
}

export default function HouseInfo() {
  const [p, setP] = useState(null);
  const [copied, setCopied] = useState("");
  const [parkView, setParkView] = useState(null);
  const [instrView, setInstrView] = useState(null);
  const [params, setParams] = useSearchParams();

  useEffect(() => { api.get("/property").then((r) => setP(r.data)).catch(() => setP({})); }, []);

  useEffect(() => {
    const instr = params.get("instr");
    if (instr === "checkout" || instr === "checkin") {
      setInstrView(instr);
      params.delete("instr");
      setParams(params, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const copy = (text, key) => {
    navigator.clipboard.writeText(text);
    setCopied(key);
    toast.success("Copied to clipboard");
    setTimeout(() => setCopied(""), 1500);
  };

  if (!p) return null;

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-8 py-8 sm:py-12 space-y-6">
      <div className="mb-2">
        <span className="overline text-brand-terracotta">Property</span>
        <h1 className="font-display text-4xl sm:text-5xl tracking-tight mt-2">House Info</h1>
      </div>

      {p.address && (
        <Section icon={MapPin} title="Address" testid="address-section">
          <p className="text-lg text-brand-forest/85 mb-3">{p.address}</p>
          <a
            href={`https://maps.google.com/?q=${encodeURIComponent(p.address)}`}
            target="_blank" rel="noreferrer"
            data-testid="address-directions"
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-brand-terracotta hover:underline"
          >
            <Navigation size={15} /> Get directions
          </a>
        </Section>
      )}

      <Section icon={LogIn} title="Check-In & Check-Out" testid="checkinout-section">
        <div className="grid grid-cols-2 gap-3 sm:gap-4">
          {[
            { key: "checkin", label: "Check-In", icon: LogIn, time: p.checkin_time, link: "Check-in instructions" },
            { key: "checkout", label: "Check-Out", icon: LogOut, time: p.checkout_time, link: "Check-out instructions" },
          ].map(({ key, label, icon: Icon, time, link }) => (
            <div key={key} data-testid={`${key}-block`} className="flex flex-col items-start rounded-2xl bg-brand-bg border border-black/5 p-5">
              <div className="flex items-center gap-2 mb-1">
                <Icon size={18} className="text-brand-terracotta" />
                <span className="overline text-brand-forest/50">{label}</span>
              </div>
              <p className="text-2xl sm:text-3xl font-display text-brand-terracotta mb-4">{time || "—"}</p>
              <button
                data-testid={`${key}-instructions-link`}
                onClick={() => setInstrView(key)}
                className="mt-auto inline-flex items-center gap-1 text-sm font-semibold text-brand-terracotta hover:underline active:scale-95 transition-transform"
              >
                {link} <ChevronRight size={15} />
              </button>
            </div>
          ))}
        </div>
      </Section>

      {((p.parking_car_images || []).length > 0 || (p.parking_rv_images || []).length > 0) && (
        <Section icon={Car} title="Where to Park" testid="parking-section">
          <p className="text-brand-forest/70 mb-4">Tap what you're driving to see exactly where to park.</p>
          <div className="grid grid-cols-2 gap-3">
            <button
              data-testid="parking-car-btn" onClick={() => setParkView("car")}
              className="flex flex-col items-center gap-2 rounded-2xl bg-brand-bg border border-black/5 p-5 hover:border-brand-terracotta active:scale-95 transition-[transform,border-color]"
            >
              <Car size={30} className="text-brand-terracotta" />
              <span className="font-semibold">Car / Truck</span>
            </button>
            <button
              data-testid="parking-rv-btn" onClick={() => setParkView("rv")}
              className="flex flex-col items-center gap-2 rounded-2xl bg-brand-bg border border-black/5 p-5 hover:border-brand-terracotta active:scale-95 transition-[transform,border-color]"
            >
              <Caravan size={30} className="text-brand-terracotta" />
              <span className="font-semibold">RV / Trailer</span>
            </button>
          </div>
        </Section>
      )}

      <Section icon={ScrollText} title="House Rules" testid="rules-section">
        <ul className="space-y-3">
          {(p.house_rules || []).map((r, i) => (
            <li key={i} className="flex items-start gap-3">
              <span className="mt-1 h-6 w-6 shrink-0 rounded-full bg-brand-sand text-brand-terracotta text-xs font-bold flex items-center justify-center">{i + 1}</span>
              <span className="text-brand-forest/80">{r}</span>
            </li>
          ))}
        </ul>
      </Section>

      <Section icon={Wifi} title="Wi-Fi" testid="wifi-section">
        <div className="grid sm:grid-cols-2 gap-4">
          {[["Network", p.wifi_name, "net"], ["Password", p.wifi_password, "pass"]].map(([label, val, key]) => (
            <button
              key={key}
              data-testid={`wifi-copy-${key}`}
              onClick={() => copy(val, key)}
              className="text-left rounded-2xl bg-brand-bg border border-black/5 p-5 hover:border-brand-terracotta transition-colors active:scale-[0.98]"
            >
              <div className="overline text-brand-forest/50 mb-1">{label}</div>
              <div className="flex items-center justify-between gap-2">
                <span className="text-lg font-semibold break-all">{val || "—"}</span>
                {copied === key ? <Check size={18} className="text-brand-terracotta shrink-0" /> : <Copy size={18} className="text-brand-forest/40 shrink-0" />}
              </div>
            </button>
          ))}
        </div>
      </Section>

      <Section icon={Phone} title="Important Contacts" testid="contacts-section">
        <div className="grid sm:grid-cols-2 gap-3">
          {(p.contacts || []).map((c, i) => (
            <a
              key={i}
              href={`tel:${c.value.replace(/[^0-9+]/g, "")}`}
              data-testid={`contact-${i}`}
              className="flex items-center justify-between rounded-2xl bg-brand-bg border border-black/5 p-4 hover:border-brand-terracotta transition-colors active:scale-[0.98]"
            >
              <div>
                <div className="overline text-brand-forest/50">{c.label}</div>
                <div className="font-semibold">{c.value}</div>
              </div>
              <Phone size={18} className="text-brand-terracotta" />
            </a>
          ))}
        </div>
        <div data-testid="camera-disclosure" className="mt-5 flex items-start gap-3 rounded-2xl bg-brand-bg border border-black/5 p-4">
          <Camera size={18} className="text-brand-terracotta shrink-0 mt-0.5" />
          <p className="text-sm text-brand-forest/70 leading-relaxed">
            <span className="font-semibold text-brand-forest">Disclosure:</span> For safety and emergency reasons only, we use 2 cameras in the back yard and one in the doorbell by the front door. Unless there is an emergency, these cameras are not activated during your stay.
          </p>
        </div>
      </Section>

      <Dialog open={!!parkView} onOpenChange={(o) => !o && setParkView(null)}>
        <DialogContent className="rounded-3xl max-w-lg max-h-[90vh] overflow-y-auto" data-testid="parking-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">{parkView === "car" ? "Car / Truck Parking" : "RV / Trailer Parking"}</DialogTitle>
          </DialogHeader>
          <p className="text-brand-forest/70 -mt-1">Please park as shown in the photo{parkView === "rv" ? "s" : ""} below.</p>
          <div className="space-y-3">
            {(parkView === "car" ? (p.parking_car_images || []) : (p.parking_rv_images || [])).map((src, i) => (
              parkView === "car" && i === 0 ? (
                <div key={i} className="space-y-2" data-testid="parking-car-options">
                  <p className="font-semibold text-brand-forest">
                    Option #1: Park on the driveway in front of the garage door.
                  </p>
                  <p className="font-semibold text-brand-forest">
                    Option #2: Park in the garage. To access the garage, you will first need to enter the house and use the garage door opener on the wall inside the garage.
                  </p>
                  <img src={mediaUrl(src)} alt="Where to park" data-testid={`parking-img-${i}`} className="w-full rounded-2xl border border-black/5" />
                </div>
              ) : parkView === "rv" && i === 0 ? (
                <div key={i} className="space-y-2" data-testid="parking-rv-option-1">
                  <p className="font-semibold text-brand-forest">
                    Option #1: Park on the south side of the concrete driveway in the gravel in front of the double gate.
                  </p>
                  <img src="/parking/rv-option1.jpg" alt="Where to park - Option 1" data-testid="parking-img-0" className="w-full rounded-2xl border border-black/5" />
                </div>
              ) : (
                parkView === "rv" && i === 1 ? (
                  <div key={i} className="space-y-2" data-testid="parking-rv-option-2">
                    <p className="font-semibold text-brand-forest">
                      Option #2: Park in the street in front of the house.
                    </p>
                    <img src={mediaUrl(src)} alt="Where to park - Option 2" data-testid={`parking-img-${i}`} className="w-full rounded-2xl border border-black/5" />
                  </div>
                ) : (
                  <img key={i} src={mediaUrl(src)} alt="Where to park" data-testid={`parking-img-${i}`} className="w-full rounded-2xl border border-black/5" />
                )
              )
            ))}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!instrView} onOpenChange={(o) => !o && setInstrView(null)}>
        <DialogContent className="rounded-3xl max-w-lg max-h-[90vh] overflow-y-auto" data-testid="instructions-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">
              {instrView === "checkin" ? "Check-In Instructions" : "Check-Out Instructions"}
            </DialogTitle>
          </DialogHeader>
          {instrView === "checkin" ? (
            <CheckinInstructions text={p.checkin_instructions} testid="instructions-body" />
          ) : (
            <p className="text-brand-forest/80 leading-relaxed whitespace-pre-line" data-testid="instructions-body">
              {p.checkout_instructions || "Instructions coming soon."}
            </p>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
